import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UserCheck, FileText, Send, Loader2, CheckCircle2, Calendar as CalIcon,
} from "lucide-react";
import api from "@/lib/api";

/**
 * "Enquadramento Global do Serviço" — pós-caução-mínima-paga.
 *
 * 05/02/2026 · Novo bloco pedido pelo utilizador:
 *   1. Consultora seleciona a Direção Técnica do escritório (dropdown).
 *   2. Gera-se o PDF de enquadramento com data de arranque, cliente,
 *      solução acordada e financeiro operacional.
 *   3. Botão manual "Enviar à DT" envia por email com o PDF em anexo.
 *   4. A DT atribuída aqui fica automaticamente definida como responsável
 *      pelo PIC (Plano Individual de Cuidados).
 *
 * Só é visível quando ``deposit.kickstart_paid`` — mesma gate do
 * RecruitmentPanel.
 */
export default function EnquadramentoPanel({ lead, onChange }) {
  const kickstartPaid = Boolean(lead?.deposit?.kickstart_paid);
  const [dts, setDts] = useState([]);
  const [gestores, setGestores] = useState([]);
  const [loadingDts, setLoadingDts] = useState(false);
  const [pickedDtId, setPickedDtId] = useState(lead?.assigned_dt_id || "");
  const [pickedGestorId, setPickedGestorId] = useState(lead?.assigned_account_manager_id || "");
  const [busy, setBusy] = useState("");
  // 05/02/2026 — Notas específicas do escritório (aparecem no PDF).
  const [officeNotes, setOfficeNotes] = useState(lead?.enquadramento_office_notes || "");
  const [notesDirty, setNotesDirty] = useState(false);

  useEffect(() => {
    setPickedDtId(lead?.assigned_dt_id || "");
    setPickedGestorId(lead?.assigned_account_manager_id || "");
  }, [lead?.assigned_dt_id, lead?.assigned_account_manager_id]);

  useEffect(() => {
    // Só substitui se o texto do backend mudou (evita apagar edições locais)
    if (!notesDirty) setOfficeNotes(lead?.enquadramento_office_notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.enquadramento_office_notes]);

  useEffect(() => {
    if (!kickstartPaid) return;
    (async () => {
      setLoadingDts(true);
      try {
        const [rDt, rGe] = await Promise.all([
          api.get("/dt-users"),
          api.get("/gestor-users"),
        ]);
        setDts(rDt.data?.items || []);
        setGestores(rGe.data?.items || []);
      } catch (e) {
        toast.error("Não consegui carregar DTs/Gestores");
      } finally {
        setLoadingDts(false);
      }
    })();
  }, [kickstartPaid]);

  if (!kickstartPaid) return null;  // Gate: só depois de caução mínima paga

  const start = lead?.deposit?.plan?.service_start_date || null;
  const assignedName = lead?.assigned_dt_name || null;
  const assignedGestorName = lead?.assigned_account_manager_name || null;
  const enquadramentoSent = !!lead?.enquadramento_sent_at;
  const assignmentChanged =
    pickedDtId !== (lead?.assigned_dt_id || "") ||
    pickedGestorId !== (lead?.assigned_account_manager_id || "");

  const saveDt = async () => {
    if (!pickedDtId) return toast.error("Escolha uma DT antes de gravar.");
    setBusy("save");
    try {
      await api.post(`/leads/${lead.id}/dt-assign`, {
        dt_id: pickedDtId,
        gestor_id: pickedGestorId || null,
      });
      toast.success(pickedGestorId
        ? "DT + Gestor de Conta atribuídos — ambos recebem o enquadramento."
        : "DT atribuída ao lead — passa a assinar o PIC.");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atribuir");
    } finally { setBusy(""); }
  };

  const previewPdf = () => {
    const url = `${api.defaults.baseURL}/leads/${lead.id}/enquadramento/pdf`;
    // Backend requires Bearer — abrimos numa nova aba com Authorization via fetch → blob
    (async () => {
      try {
        const r = await api.get(`/leads/${lead.id}/enquadramento/pdf`, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "application/pdf" });
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erro ao pré-visualizar PDF");
      }
    })();
  };

  const sendEnquadramento = async () => {
    if (!lead?.assigned_dt_id) return toast.error("Atribua uma DT antes de enviar.");
    const targets = ["DT"];
    if (lead?.assigned_account_manager_id) targets.push("Gestor de Conta");
    const msg = enquadramentoSent
      ? `Já existe um envio anterior. Reenviar o enquadramento a ${targets.join(" + ")}?`
      : `Confirmar envio do enquadramento por email a ${targets.join(" + ")}?`;
    if (!window.confirm(msg)) return;
    setBusy("send");
    try {
      const { data } = await api.post(`/leads/${lead.id}/enquadramento/send`);
      const dtSt = data?.dt_email_status?.status;
      const gestorSt = data?.gestor_email_status?.status;
      const recipients = (data?.sent_to || []).length;
      if (dtSt === "mocked") {
        toast.info(`Preview: emails a ${recipients} destinatário(s) marcados como enviados (O365 desligado).`);
      } else {
        toast.success(`Enquadramento enviado a ${recipients} destinatário(s).`);
      }
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar enquadramento");
    } finally { setBusy(""); }
  };

  const saveOfficeNotes = async () => {
    setBusy("notes");
    try {
      await api.patch(`/leads/${lead.id}/enquadramento/notes`, {
        office_notes: officeNotes,
      });
      toast.success("Notas do escritório gravadas.");
      setNotesDirty(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gravar notas");
    } finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 lg:p-6 space-y-4"
         data-testid="enquadramento-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <UserCheck className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Kick-off · Direção Técnica
          </div>
          <div className="font-display text-xl">Enquadramento Global do Serviço</div>
          <p className="text-sm text-muted-foreground">
            Atribui a DT do escritório e envia-lhe o briefing operacional (com data de arranque).
            A mesma DT passa a assinar o <b>PIC</b>.
          </p>
        </div>
        {start && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Arranque
            </div>
            <div className="text-sm font-semibold text-primary flex items-center gap-1">
              <CalIcon className="w-3.5 h-3.5" />
              {new Date(start).toLocaleDateString("pt-PT")}
            </div>
          </div>
        )}
      </div>

      {/* Passo 1 — Atribuição (DT + Gestor de Conta) */}
      <div className="rounded-lg border border-border bg-background/60 p-4 space-y-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">1</span>
          Atribuir DT + Gestor de Conta
          <span className="text-muted-foreground/70 normal-case tracking-normal font-normal ml-1">
            (ambos recebem o enquadramento por email)
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Direção Técnica
            </label>
            <select
              data-testid="dt-select"
              value={pickedDtId}
              onChange={(e) => setPickedDtId(e.target.value)}
              disabled={loadingDts || busy !== ""}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <option value="">— seleccionar DT do escritório —</option>
              {dts.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.office ? ` · ${u.office}` : ""}{u.also_dt && u.role !== "direcao" && u.role !== "dt" ? " (also_dt)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Gestor de Conta
              <span className="text-muted-foreground/70 normal-case tracking-normal font-normal ml-1">(opcional)</span>
            </label>
            <select
              data-testid="gestor-select"
              value={pickedGestorId}
              onChange={(e) => setPickedGestorId(e.target.value)}
              disabled={loadingDts || busy !== ""}
              className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <option value="">— sem gestor de conta —</option>
              {gestores.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.office ? ` · ${u.office}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex-1 space-y-0.5">
            {assignedName && (
              <div className="text-[11px] text-[hsl(var(--success))] flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>DT: <b>{assignedName}</b>
                  {lead?.assigned_dt_assigned_at && (
                    <span className="text-muted-foreground"> · desde {new Date(lead.assigned_dt_assigned_at).toLocaleDateString("pt-PT")}</span>
                  )}
                </span>
              </div>
            )}
            {assignedGestorName && (
              <div className="text-[11px] text-[hsl(var(--success))] flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Gestor de Conta: <b>{assignedGestorName}</b></span>
              </div>
            )}
          </div>
          <button
            data-testid="dt-save"
            onClick={saveDt}
            disabled={!pickedDtId || busy !== "" || !assignmentChanged}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 disabled:opacity-40"
          >
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {assignedName && !assignmentChanged ? "Atribuído" : "Atribuir"}
          </button>
        </div>
      </div>

      {/* Passo 2 (novo, 05/02/2026) — Notas específicas do escritório */}
      {lead?.assigned_dt_id && (
        <div className="rounded-lg border border-border bg-background/60 p-4 space-y-2"
             data-testid="office-notes-block">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">2</span>
            Notas específicas do escritório
            <span className="text-muted-foreground/70 normal-case tracking-normal font-normal ml-1">
              (aparecem no PDF · a DT pode anotar antes do PIC)
            </span>
          </div>
          <textarea
            data-testid="office-notes-textarea"
            value={officeNotes}
            onChange={(e) => { setOfficeNotes(e.target.value); setNotesDirty(true); }}
            placeholder="Ex: A família prefere que a cuidadora entre pela porta lateral. O escritório do Porto costuma trocar turnos ao domingo à noite. Restrições logísticas: sem estacionamento próximo…"
            rows={4}
            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm resize-y min-h-[80px]"
          />
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] text-muted-foreground">
              {lead?.enquadramento_notes_updated_at ? (
                <span>
                  Última alteração: {new Date(lead.enquadramento_notes_updated_at).toLocaleString("pt-PT")}
                  {lead.enquadramento_notes_updated_by_name ? ` por ${lead.enquadramento_notes_updated_by_name}` : ""}
                </span>
              ) : (
                <span className="text-muted-foreground/70">Sem notas ainda — a secção fica omitida do PDF.</span>
              )}
            </div>
            <button
              data-testid="office-notes-save"
              onClick={saveOfficeNotes}
              disabled={busy !== "" || !notesDirty}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 disabled:opacity-40"
            >
              {busy === "notes" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Gravar notas
            </button>
          </div>
        </div>
      )}

      {/* Passo 3 — Envio */}
      <div className={`rounded-lg border p-4 space-y-2 ${
        lead?.assigned_dt_id
          ? "border-border bg-background/60"
          : "border-dashed border-border bg-muted/30"
      }`}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">3</span>
          Enquadramento à DT
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="enq-preview"
            onClick={previewPdf}
            disabled={!lead?.assigned_dt_id || busy !== ""}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card hover:bg-muted text-sm font-semibold px-4 py-2 disabled:opacity-40"
          >
            <FileText className="w-4 h-4" /> Pré-visualizar PDF
          </button>
          <button
            data-testid="enq-send"
            onClick={sendEnquadramento}
            disabled={!lead?.assigned_dt_id || busy !== ""}
            className={`inline-flex items-center gap-1.5 rounded-md text-primary-foreground text-sm font-semibold px-4 py-2 disabled:opacity-40 ${
              enquadramentoSent
                ? "bg-muted-foreground/60 hover:bg-muted-foreground/80"
                : "bg-primary hover:bg-primary/90"
            }`}
          >
            {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {enquadramentoSent
              ? (lead?.assigned_account_manager_id ? "Reenviar a DT + Gestor" : "Reenviar à DT")
              : (lead?.assigned_account_manager_id ? "Enviar a DT + Gestor" : "Enviar à DT")}
          </button>
          {enquadramentoSent && (
            <span
              data-testid="enq-sent-chip"
              className="ml-auto self-center text-[11px] text-[hsl(var(--success))] inline-flex items-center gap-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Enviado {new Date(lead.enquadramento_sent_at).toLocaleString("pt-PT")}
              {lead.enquadramento_sent_by_name ? ` por ${lead.enquadramento_sent_by_name}` : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
